import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/utils";

/**
 * ControlPanelLoginPage — obscured, permanent admin sign-in
 * (no BrandLogo, no "MatchBroker" wordmark linking to /). Rejects
 * non-admin credentials with a generic INVALID_CREDENTIALS toast so
 * brokers who accidentally hit this URL get zero role signal.
 *
 * Route: /control-panel-x9/login → success → /control-panel-x9
 */
export default function ControlPanelLoginPage() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const genericError = "Invalid credentials.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);

    if (!result.ok) {
      setError(genericError);
      return;
    }
    // Only admin sub-roles (super_admin / verifier / support) may pass.
    // Broker/other roles: log them out and show the same generic error,
    // giving zero role-existence signal.
    if (!isAdminRole(result.user.role)) {
      logout();
      setError(genericError);
      return;
    }
    toast.success("Signed in.");
    navigate("/control-panel-x9", { replace: true });
  };

  return (
    <div
      className="mb-page flex min-h-screen items-center justify-center px-6 py-16"
      data-testid="control-panel-login"
    >
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-10 shadow-mb-card">
        <div className="mb-8 flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy/8 text-brand-navy">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
              Restricted
            </div>
            <h1 className="text-lg font-bold tracking-tight text-gray-900">
              Sign in
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="control-panel-login-form">
          <div className="space-y-2">
            <Label htmlFor="cp-email" className="text-gray-700 text-xs">
              Email
            </Label>
            <Input
              id="cp-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-focus-ring h-11 rounded-lg border-gray-200 bg-white text-sm"
              data-testid="control-panel-login-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-password" className="text-gray-700 text-xs">
              Password
            </Label>
            <div className="relative">
              <Input
                id="cp-password"
                type={showPwd ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mb-focus-ring h-11 rounded-lg border-gray-200 bg-white pr-11 text-sm"
                data-testid="control-panel-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-700"
                aria-label={showPwd ? "Hide password" : "Show password"}
                data-testid="control-panel-login-password-toggle"
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-700"
              data-testid="control-panel-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="mb-focus-ring h-11 w-full rounded-lg bg-brand-navy text-sm font-semibold text-white hover:bg-brand-navy-hover disabled:opacity-70"
            data-testid="control-panel-login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in…
              </>
            ) : (
              "Continue"
            )}
          </Button>

          <div className="text-center text-xs text-gray-500">
            <a
              href="/control-panel-x9/forgot"
              className="hover:text-gray-900"
              data-testid="control-panel-forgot-link"
            >
              Forgot password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
